import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Camera, Save, Trash2, FileSignature, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FUEL_LEVELS = ["vuoto", "1/4", "1/2", "3/4", "pieno"];
const CONDITIONS = ["ottimo", "buono", "discreto", "danneggiato"];

const DELIVERY_PHOTO_LABELS = [
  { key: "lato_destro", labelIt: "Lato destro", labelEn: "Right side" },
  { key: "lato_sinistro", labelIt: "Lato sinistro", labelEn: "Left side" },
  { key: "anteriore", labelIt: "Anteriore", labelEn: "Front" },
  { key: "posteriore", labelIt: "Posteriore", labelEn: "Rear" },
  { key: "cruscotto", labelIt: "Cruscotto/Km", labelEn: "Dashboard/Km" },
  { key: "danno", labelIt: "Danno esistente", labelEn: "Existing damage" },
];

const RETURN_PHOTO_LABELS = [
  { key: "lato_destro", labelIt: "Lato destro", labelEn: "Right side" },
  { key: "lato_sinistro", labelIt: "Lato sinistro", labelEn: "Left side" },
  { key: "anteriore", labelIt: "Anteriore", labelEn: "Front" },
  { key: "posteriore", labelIt: "Posteriore", labelEn: "Rear" },
  { key: "cruscotto", labelIt: "Cruscotto/Km finali", labelEn: "Dashboard/Final km" },
  { key: "danno_nuovo", labelIt: "Nuovo danno", labelEn: "New damage" },
];

export default function VerbalePage({ mode = "delivery" }) {
  const isDelivery = mode === "delivery";
  const { i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const { contractId } = useParams();
  const navigate = useNavigate();
  const sigRef = useRef(null);

  const [contract, setContract] = useState(null);
  const [form, setForm] = useState(isDelivery
    ? {
        km_initial: "", fuel_level: "pieno",
        helmet: true, top_box: false, chain_lock: false,
        other_accessories: "", pre_existing_damages: "", general_notes: "",
      }
    : {
        km_final: "", fuel_level: "pieno", overall_condition: "buono",
        new_damages: "", missing_accessories: "",
        extra_charges_amount: 0, extra_charges_reason: "",
        general_notes: "",
      });
  const [photos, setPhotos] = useState({}); // {label: url}
  const [uploading, setUploading] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);
  // When reopening an existing delivery verbale, we display the saved
  // signature as a preview (PNG/JPEG) instead of an empty SignaturePad.
  // The admin clicks "Sostituisci firma" to overwrite — until then,
  // saving keeps the existing signature_data_url.
  const [resignMode, setResignMode] = useState(false);

  const PHOTOS = isDelivery ? DELIVERY_PHOTO_LABELS : RETURN_PHOTO_LABELS;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let active = true;
    api.get(`/admin/contracts/${contractId}`).then(({ data }) => {
      if (!active) return;
      setContract(data);
      const existing = isDelivery ? data.delivery_report : data.return_report;
      if (existing) {
        setHasExisting(true);
        setForm((f) => ({ ...f, ...existing }));
        const ph = {};
        (existing.photos || []).forEach((p) => { ph[p.label] = p.url; });
        setPhotos(ph);
      }
    });
    return () => { active = false; };
  }, [contractId, isDelivery]);

  const uploadPhoto = async (label, file) => {
    if (!file) return;
    setUploading(label);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/uploads/report-photo`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setPhotos((p) => ({ ...p, [label]: data.url }));
      toast.success(isEN ? "Photo uploaded" : "Foto caricata");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(null);
    }
  };

  const removePhoto = (label) => {
    setPhotos((p) => {
      const next = { ...p };
      delete next[label];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Iter 42 — km optional; parse only when the admin actually filled
        // the (now-hidden) field. undefined → key deleted below.
        km_initial: isDelivery && form.km_initial ? parseInt(form.km_initial, 10) : undefined,
        km_final: !isDelivery && form.km_final ? parseInt(form.km_final, 10) : undefined,
        extra_charges_amount: !isDelivery ? parseFloat(form.extra_charges_amount || 0) : undefined,
        photos: Object.entries(photos).map(([label, url]) => ({ label, url })),
      };
      if (isDelivery && sigRef.current && !sigRef.current.isEmpty()) {
        payload.signature_data_url = sigRef.current.toDataURL();
      }
      // Remove undefined keys
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      const method = hasExisting ? "put" : "post";
      const path = `/admin/contracts/${contractId}/${isDelivery ? "delivery" : "return"}`;
      await api[method](path, payload);
      toast.success(isEN ? "Report saved" : "Verbale salvato");
      navigate("/admin/contracts");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!contract) return <div className="text-zinc-400">Loading…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid={`verbale-${mode}-page`}>
      <Button variant="ghost" onClick={() => navigate("/admin/contracts")} className="text-zinc-400 hover:text-zinc-100">
        <ArrowLeft className="w-4 h-4 mr-1" /> {isEN ? "Back" : "Indietro"}
      </Button>

      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700]">
          {isDelivery ? (isEN ? "Delivery report" : "Verbale di consegna") : (isEN ? "Return report" : "Verbale di riconsegna")}
        </p>
        <h1 className="font-display text-4xl font-bold mt-1">
          {isDelivery ? (isEN ? "Vehicle handover" : "Consegna mezzo") : (isEN ? "Vehicle return" : "Riconsegna mezzo")}
        </h1>
        <p className="text-zinc-400 text-sm mt-1">
          {contract.contract_number} · {contract.customer.name} {contract.customer.surname} · 🛵 {contract.vehicle.model} ({contract.vehicle.plate})
        </p>
      </div>

      {/* Main form */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          {/* Iter 42 — km field hidden. Rent Lupi rents with unlimited km,
              tracking them added noise. The backend still accepts optional
              km_initial/km_final for legacy contracts. */}
          <div>
            <Label className="text-zinc-300">{isEN ? "Fuel level *" : "Carburante *"}</Label>
            <Select value={form.fuel_level} onValueChange={(v) => setField("fuel_level", v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="verbale-fuel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {FUEL_LEVELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isDelivery ? (
          <div className="border-t border-zinc-800 pt-4">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">{isEN ? "Accessories" : "Accessori consegnati"}</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {["helmet", "top_box", "chain_lock"].map((k) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer p-3 rounded-lg border border-zinc-800 bg-zinc-950 hover:border-zinc-700">
                  <Checkbox checked={!!form[k]} onCheckedChange={(v) => setField(k, !!v)}
                            className="border-zinc-700 data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-zinc-950 data-[state=checked]:border-[#FFD700]"
                            data-testid={`verbale-acc-${k}`} />
                  <span className="text-sm text-zinc-200">
                    {k === "helmet" ? (isEN ? "Helmet" : "Casco")
                     : k === "top_box" ? (isEN ? "Top box" : "Bauletto")
                     : (isEN ? "Chain / disc lock" : "Catena / Bloccadisco")}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-3">
              <Label className="text-zinc-300 text-sm">{isEN ? "Other accessories" : "Altri accessori"}</Label>
              <Input value={form.other_accessories} onChange={(e) => setField("other_accessories", e.target.value)}
                     className="bg-zinc-950 border-zinc-800 mt-1" data-testid="verbale-other-acc" />
            </div>
            <div className="mt-3">
              <Label className="text-zinc-300 text-sm">{isEN ? "Pre-existing damages" : "Danni preesistenti"}</Label>
              <Textarea rows={2} value={form.pre_existing_damages} onChange={(e) => setField("pre_existing_damages", e.target.value)}
                        className="bg-zinc-950 border-zinc-800 mt-1" data-testid="verbale-pre-damages" />
            </div>
          </div>
        ) : (
          <div className="border-t border-zinc-800 pt-4 space-y-3">
            <div>
              <Label className="text-zinc-300">{isEN ? "Overall condition" : "Stato generale"}</Label>
              <Select value={form.overall_condition} onValueChange={(v) => setField("overall_condition", v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="verbale-condition">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">{isEN ? "New damages found" : "Nuovi danni riscontrati"}</Label>
              <Textarea rows={2} value={form.new_damages} onChange={(e) => setField("new_damages", e.target.value)}
                        className="bg-zinc-950 border-zinc-800 mt-1" data-testid="verbale-new-damages" />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">{isEN ? "Missing accessories" : "Accessori mancanti"}</Label>
              <Input value={form.missing_accessories} onChange={(e) => setField("missing_accessories", e.target.value)}
                     className="bg-zinc-950 border-zinc-800 mt-1" data-testid="verbale-missing" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-zinc-800">
              <div>
                <Label className="text-zinc-300 text-sm">{isEN ? "Extra charges (€)" : "Addebito cliente (€)"}</Label>
                <Input type="number" step="0.01" value={form.extra_charges_amount}
                       onChange={(e) => setField("extra_charges_amount", e.target.value)}
                       className="bg-zinc-950 border-zinc-800 mt-1" data-testid="verbale-charges-amt" />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">{isEN ? "Reason" : "Motivo"}</Label>
                <Input value={form.extra_charges_reason} onChange={(e) => setField("extra_charges_reason", e.target.value)}
                       className="bg-zinc-950 border-zinc-800 mt-1" data-testid="verbale-charges-reason" />
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-zinc-800 pt-4">
          <Label className="text-zinc-300 text-sm">{isEN ? "Notes" : "Note generali"}</Label>
          <Textarea rows={3} value={form.general_notes} onChange={(e) => setField("general_notes", e.target.value)}
                    className="bg-zinc-950 border-zinc-800 mt-1" data-testid="verbale-notes" />
        </div>
      </div>

      {/* Photos */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Camera className="w-4 h-4 text-[#FFD700]" />
          <h2 className="font-semibold text-zinc-100">{isEN ? "Photos" : "Fotografie"}</h2>
          <span className="text-xs text-zinc-500 ml-auto">{Object.keys(photos).length}/{PHOTOS.length}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {PHOTOS.map((p) => {
            const url = photos[p.key];
            const fullUrl = url?.startsWith("http") ? url : `${BACKEND_URL}${url}`;
            return (
              <div key={p.key} className="border border-zinc-800 rounded-lg overflow-hidden bg-zinc-950" data-testid={`photo-slot-${p.key}`}>
                {url ? (
                  <div className="relative aspect-[4/3] bg-zinc-900">
                    <img src={fullUrl} alt={p.key} className="w-full h-full object-cover" />
                    <button onClick={() => removePhoto(p.key)} className="absolute top-1 right-1 bg-red-500/80 hover:bg-red-500 text-white rounded-full p-1">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="aspect-[4/3] flex items-center justify-center cursor-pointer hover:bg-zinc-900/60 transition">
                    {uploading === p.key
                      ? <span className="text-xs text-zinc-500">…</span>
                      : <div className="text-center text-zinc-600">
                          <Upload className="w-5 h-5 mx-auto mb-1" />
                          <span className="text-[10px] uppercase tracking-wider">{isEN ? "Add" : "Carica"}</span>
                        </div>}
                    <input type="file" accept="image/*" capture="environment"
                           onChange={(e) => uploadPhoto(p.key, e.target.files?.[0])}
                           hidden data-testid={`photo-upload-${p.key}`} />
                  </label>
                )}
                <p className="px-2 py-1.5 text-[11px] text-zinc-400 bg-zinc-900/40 text-center font-medium">
                  {isEN ? p.labelEn : p.labelIt}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Signature (delivery only) */}
      {isDelivery && (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6">
          <div className="flex items-center gap-2 mb-3">
            <FileSignature className="w-4 h-4 text-[#FFD700]" />
            <h2 className="font-semibold text-zinc-100">{isEN ? "Customer signature at delivery" : "Firma cliente alla consegna"}</h2>
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            {isEN ? "Optional — separate from the rental contract signature." : "Opzionale — separata dalla firma del contratto."}
          </p>
          {form.signature_data_url && !resignMode ? (
            <div className="space-y-2" data-testid="verbale-signature-preview">
              <div className="rounded-lg border border-emerald-500/30 bg-white p-2">
                <img
                  src={form.signature_data_url}
                  alt="Firma esistente"
                  className="max-h-[200px] mx-auto"
                  data-testid="verbale-signature-image"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setResignMode(true)}
                className="text-zinc-400 hover:text-[#FFD700]"
                data-testid="verbale-signature-resign"
              >
                {isEN ? "Replace signature" : "Sostituisci firma"}
              </Button>
            </div>
          ) : (
            <SignaturePad ref={sigRef} height={200} />
          )}
        </div>
      )}

      {!isDelivery && parseFloat(form.extra_charges_amount || 0) > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="text-sm text-amber-100">
            {isEN ? "Customer will be charged" : "Al cliente verrà addebitato"}{" "}
            <span className="font-bold text-amber-300">€ {parseFloat(form.extra_charges_amount).toFixed(2)}</span>
            {form.extra_charges_reason ? <> — {form.extra_charges_reason}</> : null}
          </div>
        </div>
      )}

      <div className="sticky bottom-4 flex justify-between bg-zinc-950/80 backdrop-blur p-3 rounded-xl">
        <Button variant="ghost" onClick={() => navigate("/admin/contracts")} className="text-zinc-400">
          {isEN ? "Cancel" : "Annulla"}
        </Button>
        <Button onClick={save} disabled={saving} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="verbale-save">
          <Save className="w-4 h-4 mr-1" />
          {saving ? (isEN ? "Saving…" : "Salvataggio…") : (hasExisting ? (isEN ? "Update report" : "Aggiorna verbale") : (isEN ? "Save report" : "Salva verbale"))}
        </Button>
      </div>
    </div>
  );
}
